/**
 * safeConsole.ts
 *
 * Safe console logging utilities that only log in development environments.
 * Use these instead of direct console.* calls to prevent logs in production.
 *
 * `error` logs in all environments (for debugging) but is rate limited per
 * message: the same error may print at most ERROR_BURST_LIMIT times per
 * window, then only a periodic suppression summary. Without this, one
 * repeating failure in a loop (retry wave, realtime reconnect) can print
 * thousands of console errors, which PostHog replays count as a broken,
 * locked session.
 */

import { isDev } from './isDev'

const ERROR_BURST_LIMIT = 3
const ERROR_WINDOW_MS = 60_000

type ErrorLogWindow = {
  count: number
  startedAt: number
  suppressed: number
}

const errorLogWindows = new Map<string, ErrorLogWindow>()

function errorMessageKey(args: unknown[]): string {
  const first = args[0]
  if (typeof first === 'string') return first
  if (first instanceof Error) return `${first.name}: ${first.message}`
  return 'non-string-error'
}

function shouldPrintError(args: unknown[]): boolean {
  const now = Date.now()
  const key = errorMessageKey(args)
  const existing = errorLogWindows.get(key)

  if (!existing || now - existing.startedAt >= ERROR_WINDOW_MS) {
    if (existing && existing.suppressed > 0) {
      console.error(
        `[safeConsole] suppressed ${existing.suppressed} repeat(s) of: ${key}`
      )
    }
    errorLogWindows.set(key, { count: 1, startedAt: now, suppressed: 0 })
    return true
  }

  existing.count += 1
  if (existing.count <= ERROR_BURST_LIMIT) {
    return true
  }

  existing.suppressed += 1
  return false
}

/**
 * Safe console wrapper that only outputs logs in development environment
 */
export const safeConsole = {
  log: (...args: any[]): void => {
    if (isDev) {
      console.log(...args)
    }
  },

  info: (...args: any[]): void => {
    if (isDev) {
      console.info(...args)
    }
  },

  warn: (...args: any[]): void => {
    if (isDev) {
      console.warn(...args)
    }
  },

  error: (...args: any[]): void => {
    // Errors should be logged in all environments for debugging, but a
    // repeating error in a loop must not flood the console (see header).
    if (shouldPrintError(args)) {
      console.error(...args)
    }
  },

  debug: (...args: any[]): void => {
    if (isDev) {
      console.debug(...args)
    }
  },

  trace: (...args: any[]): void => {
    if (isDev) {
      console.trace(...args)
    }
  }
}
